import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePushNotification } from '@/hooks/usePushNotification';
import { useNotificationPreferences } from '@/api/queries/notifications';

const DISMISS_KEY = 'push-banner-dismissed';

export function PushNotificationBanner() {
  const { isSupported, isSubscribing, permission, subscribe } = usePushNotification();
  const { data: prefData } = useNotificationPreferences();
  const [dismissed, setDismissed] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY)) {
      setDismissed(true);
    }
  }, []);

  // Don't show if:
  // - Push not supported in browser
  // - Push channel not available on server
  // - Permission already granted or denied
  // - Dismissed this session
  // - Already subscribed
  const pushAvailable = prefData?.channels?.push ?? false;
  if (
    !isSupported ||
    !pushAvailable ||
    permission !== 'default' ||
    dismissed ||
    subscribed
  ) {
    return null;
  }

  async function handleActivate() {
    const success = await subscribe();
    if (success) {
      setSubscribed(true);
    } else {
      // Permission denied or error — hide permanently
      setDismissed(true);
      sessionStorage.setItem(DISMISS_KEY, '1');
    }
  }

  function handleDismiss() {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, '1');
  }

  return (
    <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-50 max-w-sm bg-white rounded-xl shadow-lg border border-slate-200 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-lagune-100 flex items-center justify-center">
          <Bell className="w-5 h-5 text-brand-lagune-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            Notifications push
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Recevez des alertes même quand l'application est fermée.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              onClick={handleActivate}
              loading={isSubscribing}
              className="bg-brand-lagune-700 hover:bg-brand-lagune-800 text-xs"
            >
              Activer
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="text-xs text-slate-500"
            >
              Plus tard
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-slate-400 hover:text-slate-600"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
